import { z } from "zod/v4";

const money = z
  .number()
  .finite()
  .min(0, "Enter zero or a positive amount.")
  .max(1e12, "Use an amount no greater than 1 trillion.")
  .multipleOf(0.01, "Use no more than two decimal places.");
const rate = z
  .number()
  .finite()
  .min(0, "Use an annual rate from 0 to 100%.")
  .max(100, "Use an annual rate from 0 to 100%.");
const months = z
  .number()
  .int("Use a whole number of months.")
  .min(1, "Use a term from 1 to 600 months.")
  .max(600, "Use a term from 1 to 600 months.");
const cents = (amount: number): number => Math.round(amount * 100);
export const fundingSchema = z
  .object({
    price: money.positive("Enter a purchase price above zero."),
    fees: money,
    workingCapital: money,
    reserve: money,
    bankAmount: money,
    bankRate: rate,
    bankMonths: months,
    sellerAmount: money,
    sellerRate: rate,
    sellerMonths: months,
    sellerInterestOnly: z.boolean(),
    partnerEquity: money,
    availableCash: money.nullable(),
  })
  .superRefine((value, ctx) => {
    const uses =
      cents(value.price) +
      cents(value.fees) +
      cents(value.workingCapital) +
      cents(value.reserve);
    if (
      cents(value.bankAmount) +
        cents(value.sellerAmount) +
        cents(value.partnerEquity) >
      uses
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["bankAmount"],
        message:
          "Loans and partner equity exceed the total project cost. Reduce a funding amount.",
      });
    }
    if (value.sellerAmount > value.price) {
      ctx.addIssue({
        code: "custom",
        path: ["sellerAmount"],
        message:
          "Seller financing cannot exceed the purchase price in this model.",
      });
    }
  });

export type FundingInput = z.infer<typeof fundingSchema>;
export type NumericField = Exclude<keyof FundingInput, "sellerInterestOnly">;
export type FundingDraft = Record<NumericField, string> & {
  sellerInterestOnly: boolean;
};
export type FundingErrors = Partial<Record<keyof FundingInput, string>>;
export type LoanEstimate = {
  monthly: number;
  interest: number;
  balloon: number;
  totalRepaid: number;
};
export type FundingEstimate = {
  input: FundingInput;
  projectCost: number;
  buyerCash: number;
  bank: LoanEstimate;
  seller: LoanEstimate;
  monthly: number;
  totalInterest: number;
  cashGap: number | null;
  stressedMonthly: number;
};

/** Accept decimal points and correctly grouped thousands, without guessing decimal commas. */
function parseDraftNumber(raw: string): number {
  const value = raw.trim();
  const normalized = /^[+-]?\d{1,3}(?:,\d{3})+(?:\.\d*)?$/.test(value)
    ? value.replace(/,/g, "")
    : value;
  return /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(normalized)
    ? Number(normalized)
    : Number.NaN;
}

export const initialFundingDraft: FundingDraft = {
  price: "300000",
  fees: "15000",
  workingCapital: "25000",
  reserve: "20000",
  bankAmount: "240000",
  bankRate: "10",
  bankMonths: "120",
  sellerAmount: "0",
  sellerRate: "6",
  sellerMonths: "60",
  sellerInterestOnly: false,
  partnerEquity: "0",
  availableCash: "",
};

/** Nominal annual rate, monthly payments in arrears, no fees or rate changes. */
export function estimateLoan(
  principal: number,
  annualRate: number,
  termMonths: number,
  interestOnly = false,
): LoanEstimate {
  if (principal === 0)
    return { monthly: 0, interest: 0, balloon: 0, totalRepaid: 0 };
  const r = annualRate / 1200;
  const monthly = interestOnly
    ? principal * r
    : r === 0
      ? principal / termMonths
      : (principal * r) / -Math.expm1(-termMonths * Math.log1p(r));
  const balloon = interestOnly ? principal : 0;
  const totalRepaid = monthly * termMonths + balloon;
  return {
    monthly,
    interest: Math.max(0, totalRepaid - principal),
    balloon,
    totalRepaid,
  };
}

export function calculateFunding(
  draft: FundingDraft,
): { ok: true; value: FundingEstimate } | { ok: false; errors: FundingErrors } {
  const values = Object.fromEntries(
    Object.entries(draft).map(([key, value]) => [
      key,
      typeof value === "boolean"
        ? value
        : value.trim() === ""
          ? key === "availableCash"
            ? null
            : Number.NaN
          : parseDraftNumber(value),
    ]),
  );
  const parsed = fundingSchema.safeParse(values);
  if (!parsed.success) {
    const errors: FundingErrors = {};
    for (const issue of parsed.error.issues) {
      const key = issue.path[0] as keyof FundingInput;
      errors[key] ??=
        issue.code === "invalid_type" ? "Enter a valid number." : issue.message;
    }
    return { ok: false, errors };
  }
  const input = parsed.data;
  const projectCents =
    cents(input.price) +
    cents(input.fees) +
    cents(input.workingCapital) +
    cents(input.reserve);
  const projectCost = projectCents / 100;
  const buyerCash =
    (projectCents -
      cents(input.bankAmount) -
      cents(input.sellerAmount) -
      cents(input.partnerEquity)) /
    100;
  const bank = estimateLoan(input.bankAmount, input.bankRate, input.bankMonths);
  const seller = estimateLoan(
    input.sellerAmount,
    input.sellerRate,
    input.sellerMonths,
    input.sellerInterestOnly,
  );
  return {
    ok: true,
    value: {
      input,
      projectCost,
      buyerCash,
      bank,
      seller,
      monthly: bank.monthly + seller.monthly,
      totalInterest: bank.interest + seller.interest,
      cashGap:
        input.availableCash === null
          ? null
          : (cents(buyerCash) - cents(input.availableCash)) / 100,
      stressedMonthly:
        estimateLoan(input.bankAmount, input.bankRate + 2, input.bankMonths)
          .monthly + seller.monthly,
    },
  };
}

export function formatFundingMoney(
  value: number,
  currency: string,
  decimals = Number.isInteger(value) ? 0 : 2,
): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}
